'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { validateCPF, validateCNPJ } from '@/lib/mockData';
import { useAuth } from '@/contexts/AuthContext';

interface SignupFormData {
  name: string;
  email: string;
  company: string;
  docType: 'CPF' | 'CNPJ';
  docNumber: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>({
    defaultValues: { docType: 'CPF' },
  });

  const docType = watch('docType');
  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.name,
        company: data.company,
        docType: data.docType,
        docNumber: data.docNumber,
      });
      // Redirect to email confirmation with onboarding flag
      const params = new URLSearchParams({ email: data.email, onboarding: '1' });
      router.push(`/email-confirmation?${params.toString()}`);
    } catch (err: any) {
      const message = err?.message || '';
      if (message === 'EMAIL_ALREADY_REGISTERED' || message === 'User already registered' || message.toLowerCase().includes('user already registered') || message.toLowerCase().includes('email already')) {
        setError('email', { message: 'Este e-mail já está cadastrado. Tente fazer login.' });
      } else {
        toast.error(traduzirErro(message || 'Erro ao criar conta. Tente novamente.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateDoc = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (docType === 'CPF') {
      return validateCPF(cleaned) || 'CPF inválido — verifique os dígitos verificadores';
    }
    return validateCNPJ(cleaned) || 'CNPJ inválido — verifique os dígitos verificadores';
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
    focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
    ${hasError ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`;

  function traduzirErro(message: string | undefined): string {
    if (!message) return '';
    const m = message.toLowerCase();
    if (m.includes('user already registered') || m.includes('email already')) return 'Este e-mail já está cadastrado. Tente fazer login.';
    if (m.includes('invalid email')) return 'E-mail inválido.';
    if (m.includes('password should be at least')) return 'A senha deve ter pelo menos 6 caracteres.';
    if (m.includes('weak password')) return 'Senha muito fraca. Use letras maiúsculas, minúsculas, números e símbolos.';
    if (m.includes('too many requests') || m.includes('rate limit')) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    if (m.includes('network') || m.includes('fetch')) return 'Erro de conexão. Verifique sua internet e tente novamente.';
    if (m.includes('signup disabled')) return 'Cadastro temporariamente desabilitado.';
    return message;
  }

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Criar sua conta</h2>
        <p className="text-white/50 text-[14px] mt-1">
          Comece a enviar documentos para assinatura em minutos
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">
            Nome completo
          </label>
          <input
            type="text"
            placeholder="Seu nome completo"
            {...register('name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
            className={inputClass(!!errors.name)}
          />
          {errors.name && <p className="text-red-400 text-[12px] mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">E-mail corporativo</label>
          <input
            type="email"
            placeholder="seu@empresa.com.br"
            {...register('email', {
              required: 'E-mail é obrigatório',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
            })}
            className={inputClass(!!errors.email)}
          />
          {errors.email && <p className="text-red-400 text-[12px] mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">Empresa / Organização</label>
          <input
            type="text"
            placeholder="Nome da sua empresa"
            {...register('company', { required: 'Empresa é obrigatória' })}
            className={inputClass(!!errors.company)}
          />
          {errors.company && <p className="text-red-400 text-[12px] mt-1">{errors.company.message}</p>}
        </div>

        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">Documento</label>
          <div className="flex gap-2">
            <select
              {...register('docType')}
              className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/5 text-white text-[14px] outline-none focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db] w-24 shrink-0"
            >
              <option value="CPF" className="bg-[#0a1628] text-white">CPF</option>
              <option value="CNPJ" className="bg-[#0a1628] text-white">CNPJ</option>
            </select>
            <input
              type="text"
              placeholder={docType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
              {...register('docNumber', {
                required: `${docType} é obrigatório`,
                validate: validateDoc,
              })}
              className={`flex-1 px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                ${errors.docNumber ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
            />
          </div>
          {errors.docNumber && <p className="text-red-400 text-[12px] mt-1">{errors.docNumber.message}</p>}
        </div>

        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">Senha</label>
          <p className="text-[11px] text-white/40 mb-1.5">Mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Crie uma senha forte"
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                validate: (value) => {
                  if (!/[A-Z]/.test(value)) return 'A senha deve conter pelo menos uma letra maiúscula';
                  if (!/[a-z]/.test(value)) return 'A senha deve conter pelo menos uma letra minúscula';
                  if (!/[0-9]/.test(value)) return 'A senha deve conter pelo menos um número';
                  if (!/[^A-Za-z0-9]/.test(value)) return 'A senha deve conter pelo menos um caractere especial (ex: @, #, !, $)';
                  return true;
                },
              })}
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                ${errors.password ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
            />
            <button type="button" onClick={() => setShowPassword(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-red-400 text-[12px] mt-1">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">Confirmar senha</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repita a senha"
              {...register('confirmPassword', {
                required: 'Confirmação é obrigatória',
                validate: v => v === password || 'As senhas não coincidem',
              })}
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                ${errors.confirmPassword ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
            />
            <button type="button" onClick={() => setShowConfirm(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-red-400 text-[12px] mt-1">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            {...register('terms', { required: 'Aceite os termos para continuar' })}
            className="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/5 cursor-pointer"
          />
          <label htmlFor="terms" className="text-[12.5px] text-white/50 cursor-pointer leading-relaxed">
            Concordo com os{' '}
            <a href="#" className="text-[#60a5fa] hover:underline">Termos de Uso</a>{' '}
            e a{' '}
            <a href="#" className="text-[#60a5fa] hover:underline">Política de Privacidade</a>{' '}
            da RD2Sign
          </label>
        </div>
        {errors.terms && <p className="text-red-400 text-[12px]">{errors.terms.message}</p>}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-[#1a56db] hover:bg-[#1e40af] text-white rounded-lg font-600 text-[14px]
            shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50
            active:scale-[0.98] transition-all duration-150
            disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <><Loader2 size={16} className="animate-spin" />Criando conta...</>
          ) : 'Assinar Plano'}
        </button>
      </form>

      <p className="text-center text-[13px] text-white/50 mt-5">
        Já tem uma conta?{' '}
        <button onClick={onSwitchToLogin} className="text-[#60a5fa] font-600 hover:underline">
          Entrar
        </button>
      </p>
    </div>
  );
}